import axios from 'axios';
import { LOGOUT } from '../Container/Login/constants';
import store from '../store';
import { isTokenExpired, clearAuthData } from './tokenUtils';

const env = process.env.NODE_ENV;

const version = 'v1.0'

const api = axios.create({
    baseURL:
        env === 'production'
            ? `https://admin.zedblox.com/api/${version}`//`https://console.zedblox.com/api/${version}`
            : `/api/${version}`,
    headers: {
        'Content-Type': 'application/json'
    },
    timeout: 30000 // 30 second timeout
});

/**
 * Request interceptor to check token validity before making requests
 */
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('token');
        
        // Check if token exists and is valid before making the request
        if (token) {
            if (isTokenExpired(token)) {
                // Token is expired - log warning but let the request proceed
                // The server will validate and return 401 if truly expired
                // This avoids false positives from client-side time drift
                console.warn('API: Token may be expired, letting server validate');
            }
            // Always set the token in headers if it exists
            // Let the server be the source of truth for token validity
            config.headers['x-auth-token'] = token;
        }
        
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

/**
 * Response interceptor to handle auth errors
 * Intercept any error responses from the api and check if the token is no longer valid.
 * ie. Token has expired or user is no longer authenticated.
 * Logout the user if the token has expired
 */
api.interceptors.response.use(
    res => res,
    err => {
        // Handle network errors gracefully without triggering logout
        if (!err.response) {
            console.error('API: Network error - no response received');
            return Promise.reject({ 
                response: { 
                    data: { msg: 'Network error. Please check your connection.' },
                    status: 0
                },
                isNetworkError: true
            });
        }
        
        // Handle 401 Unauthorized - but check if it's truly a token issue
        if (err.response.status === 401) {
            const errorMsg = err.response?.data?.msg?.toLowerCase() || '';
            const isTokenError = 
                errorMsg.includes('token') || 
                errorMsg.includes('authorization') ||
                errorMsg.includes('no token') ||
                errorMsg.includes('expired') ||
                errorMsg.includes('invalid');
            
            // Only dispatch LOGOUT if it's a clear token/auth error
            // Don't logout for other 401 errors (like wrong credentials on login)
            if (isTokenError && localStorage.getItem('token')) {
                console.warn('API: 401 - Token issue detected, logging out');
                clearAuthData();
                store.dispatch({ type: LOGOUT });
            } else {
                console.warn('API: 401 - Non-token auth error');
            }
        }
        
        // Handle 403 Forbidden - token valid but access denied (don't logout)
        if (err.response.status === 403) {
            console.warn('API: 403 Forbidden - Access denied (not logging out)');
        }
        
        return Promise.reject(err);
    }
);

export default api;